import { Chip, ChipProps } from "@mui/material";
import { COLORS } from "assets";
import { useMemo } from "react";

export type StatusPillTone =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "neutral"
  | "reported"
  | "super"
  | "admin";

const TONE_STYLES: Record<StatusPillTone, { bg: string; color: string }> = {
  success: { bg: "rgba(9, 185, 133, 0.14)", color: COLORS.success },
  warning: { bg: "rgba(245, 158, 11, 0.16)", color: COLORS.warning },
  error: { bg: "rgba(255, 77, 94, 0.14)", color: COLORS.error },
  info: { bg: "rgba(30, 152, 217, 0.14)", color: COLORS.primary },
  reported: { bg: "#F5F3FF", color: "#8B5CF6" },
  neutral: { bg: "rgba(98, 108, 122, 0.12)", color: COLORS.surface.secondaryText },
  super: { bg: "#F3E8FF", color: "#7C3AED" },
  admin: { bg: "#E0F2FE", color: "#0284C7" }
};

const STATUS_TONE: Record<string, StatusPillTone> = {
  Resolved: "success",
  "Action Required": "error",
  Reported: "reported",
  running: "success",
  connected: "success",
  loading: "info",
  off: "neutral",
  failed: "error",
  disconnected: "neutral",
  invalid: "error",
  healthy: "success",
  down: "error",
  unknown: "neutral",
  super: "super",
  admin: "admin",
  user: "neutral"
};

export interface StatusPillProps extends Omit<ChipProps, "color"> {
  label: string;
  tone?: StatusPillTone;
  status?: string;
}

export default function StatusPill({ label, tone, status, sx, ...rest }: StatusPillProps) {
  const resolved = useMemo(() => {
    const key = tone || (status ? STATUS_TONE[status] : undefined) || "neutral";
    return TONE_STYLES[key];
  }, [tone, status]);

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        bgcolor: resolved.bg,
        color: resolved.color,
        fontWeight: 600,
        fontSize: 12,
        height: 28,
        borderRadius: `${COLORS.radius.pill}px`,
        border: "none",
        "& .MuiChip-label": { px: 1.5 },
        ...sx
      }}
      {...rest}
    />
  );
}
